import { useEffect, useState, type ReactNode } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import {
  anularVisita, corregirVisita, getContactosVisitas, listCampaniasLevantamiento,
  listCorreccionesDeVisita, listMarcasLevantamiento, listMercadosLevantamiento,
  listVisitasBandeja, resolverHallazgo,
  type CampaniaLevantamiento, type CorreccionVisitaRow, type MercadoLevantamiento,
  type VisitaHallazgoRow, type VisitaMedicionRow, type VisitaPrecioRow, type VisitaRow,
} from '../../api/levantamiento';
import { campaniasQueCoordino } from '../../api/campaigns';
import { getPhotoUrl } from '../../api/attendance';
import { useAuth } from '../../components/AuthProvider';
// El campo con casilla «cambiar» es el MISMO que usa la bandeja de canjes: las dos pantallas hacen
// en pantalla la misma promesa sobre los campos sin marcar, y escrita dos veces se separa.
import { CampoCorregible } from '../../components/CampoCorregible';
import { puedeEditar } from '../../domain/permissions';
import {
  CATEGORIAS, CLIENTES, COLUMNA_DE_SECCION, GIROS, MOTIVOS_NO_PUDE, MOTIVOS_RECOMENDACION,
  PRECIO_POR_PREDETERMINADO, SECCIONES_OBSERVABLES, TITULO_DE_SECCION, etiquetaCategoria,
  ppuFueraDeRango, unidadesEfectivas,
  type CategoriaProducto, type MarcaLevantamiento, type Presencia, type SeccionObservable,
} from '../../domain/levantamiento';

const TZ = 'America/Lima';

const etiqueta = (
  lista: readonly { valor: string; label: string }[], valor: string | null,
): string => (valor === null ? '—' : lista.find((x) => x.valor === valor)?.label ?? valor);

/**
 * Un rótulo de color con su explicación. Los tres usos —presencia, hallazgo, lado de la marca—
 * comparten la forma porque comparten la promesa: **ningún color dice algo que el texto no diga**.
 * Una celda que solo se distingue por el color no la puede leer quien no distingue esos dos colores,
 * y este tablero se proyecta en una reunión.
 */
export interface Insignia {
  label: string;
  /** Lo que se lee al posar el cursor. Es donde vive el matiz que no cabe en dos palabras. */
  titulo: string;
  clase: string;
}

// ---------------------------------------------------------------------------
// Tiene / Se le acabó / No la trabaja
// ---------------------------------------------------------------------------

/**
 * Las tres respuestas de la cuadrícula, cada una con SU color.
 *
 * POR QUÉ CAMBIÓ. Hasta hoy las tres se pintaban con el mismo gris y el mismo tamaño, así que había
 * que leer marca por marca para encontrar un quiebre: **«no la trabaja» y «se le acabó» son hechos
 * opuestos** —uno es un puesto que decidió no tener la marca, el otro es venta perdida con la marca
 * ya adentro, que se resuelve con una llamada al distribuidor— y a la misma distancia se leen igual.
 * El quiebre hoy sale en cero en todo lo medido; cuando aparezca el primero tiene que saltar a la
 * vista sin que nadie lo vaya a buscar.
 *
 * Los textos son más CORTOS que los de `PRESENCIAS` (dominio) a propósito, y no es una tercera
 * versión suelta: allá los lee la auditora en un celular, de una en una y con espacio de sobra
 * («Lo trabaja pero está agotado»); acá se repiten seis veces por fila dentro de una tabla de diez
 * columnas. La frase completa vive en `titulo`, así que el matiz no se pierde: se mueve.
 *
 * `Record<Presencia, …>` y no `Record<string, …>`: si el dominio agrega una cuarta presencia, esto
 * deja de compilar en vez de pintarla en gris como si fuera una más.
 */
const PRESENCIA_INSIGNIA: Record<Presencia, Insignia> = {
  presente: {
    label: 'Tiene',
    titulo: 'Lo tiene hoy en el puesto.',
    clase: 'bg-emerald-100 text-emerald-900',
  },
  quiebre: {
    label: 'Se le acabó',
    titulo: 'Lo trabaja pero hoy estaba agotado. Es un quiebre: la marca ya está adentro y la venta '
      + 'se pierde igual.',
    clase: 'bg-orange-200 font-medium text-orange-900',
  },
  no_trabaja: {
    label: 'No la trabaja',
    titulo: 'No vende esa marca. NO es un quiebre: acá no hay nada agotado, hay una marca que este '
      + 'puesto no tiene.',
    clase: 'bg-gray-100 text-gray-600',
  },
};

function esPresencia(v: string): v is Presencia {
  return Object.prototype.hasOwnProperty.call(PRESENCIA_INSIGNIA, v);
}

/**
 * Cómo se pinta una presencia. Lo que no reconoce se pinta con su propio valor a la vista.
 *
 * `presencia` llega como `string` desde la API —es lo que devuelve la columna— y un valor que esta
 * pantalla no conozca tiene que VERSE, no desaparecer: es el síntoma de que la base y el código se
 * separaron, y en gris claro sin texto nadie lo notaría nunca.
 */
export function insigniaDePresencia(presencia: string): Insignia {
  if (esPresencia(presencia)) return PRESENCIA_INSIGNIA[presencia];
  return {
    label: presencia,
    titulo: 'Esta pantalla no conoce esa respuesta. Está tal como la guardó el servidor.',
    clase: 'bg-yellow-100 text-yellow-900',
  };
}

/**
 * Las banderas, con lo que significan. Ninguna es un error: son calidad de dato a la vista.
 * `sin_gps` va a ser la más frecuente y es la más inofensiva — dentro de un mercado de abastos
 * no hay cobertura, y por eso los mercados se crearon sin geocerca.
 */
const BANDERA_LABEL: Record<string, { label: string; titulo: string }> = {
  sin_gps: {
    label: 'Sin GPS',
    titulo: 'No se pudo capturar la ubicación. Es lo esperado dentro de un mercado.',
  },
  fuera_de_programacion: {
    label: 'Sin programación',
    titulo: 'No había asignación ese día. En este módulo es lo normal: el mercado se elige.',
  },
  sin_asistencia: {
    label: 'Sin asistencia',
    titulo: 'No marcó ingreso ese día.',
  },
  registro_tardio: {
    label: 'Registro tardío',
    titulo: 'Se subió después del día que declara.',
  },
  ppu_fuera_de_rango: {
    label: 'Precio por unidad raro',
    titulo: 'Algún precio dividido entre sus unidades cae fuera de S/ 0.30–3.00. '
      + 'Casi siempre es el precio del paquete anotado como si fuera el de la unidad.',
  },
};

// ---------------------------------------------------------------------------
// Marca nuestra o de la competencia
// ---------------------------------------------------------------------------

/** La clave con la que se compara un nombre de marca. Recortado y en minúsculas: ver el índice. */
export const claveDeMarca = (nombre: string): string => nombre.trim().toLowerCase();

/**
 * De qué lado está cada marca de la campaña, indexada por su nombre.
 *
 * POR QUÉ POR NOMBRE Y NO POR ID, que sería lo obvio. La bandeja recibe cada medición y cada precio
 * con el NOMBRE de la marca ya resuelto (`VisitaMedicionRow.marcaNombre`,
 * `VisitaPrecioRow.marcaNombre`) y sin el id: la consulta los trae por embed y en el precio el
 * nombre puede venir de `marca_declarada`, que es texto tecleado y no apunta a ninguna fila. Con lo
 * que hay en pantalla, el nombre es la única llave posible.
 *
 * LO QUE ESO CUESTA, dicho para que nadie lo descubra leyendo un reporte: una marca **tecleada a
 * mano** que se llame igual que una de la lista se va a rotular como esa. Es el mismo riesgo que ya
 * existe en la fila «Marca económica» —cuyo nombre real lo declara la auditora en cada visita— y por
 * eso el rótulo se elige con `insigniaDeLado`, que ante la duda no rotula nada en vez de inventar un
 * lado. Un «nuestra» equivocado en un reporte de participación es peor que un hueco: el hueco se
 * pregunta, el rótulo se cree.
 *
 * DOS MARCAS CON EL MISMO NOMBRE Y DISTINTO LADO SE DESCARTAN. No debería pasar dentro de una
 * campaña, pero si pasa —una marca duplicada, una migración a medias— quedarse con la última leída
 * sería elegir un lado al azar y pintarlo con la misma confianza que el resto.
 */
export function indiceDeMarcasPropias(
  marcas: readonly MarcaLevantamiento[],
): Map<string, boolean> {
  const out = new Map<string, boolean>();
  const enConflicto = new Set<string>();
  for (const m of marcas) {
    const clave = claveDeMarca(m.nombre);
    if (clave === '') continue;
    const previo = out.get(clave);
    if (previo !== undefined && previo !== m.esPropia) { enConflicto.add(clave); continue; }
    out.set(clave, m.esPropia);
  }
  for (const c of enConflicto) out.delete(c);
  return out;
}

/** Índice vacío: nada se rotula. Es una constante para no crear un `Map` por fila pintada. */
export const SIN_LADOS: ReadonlyMap<string, boolean> = new Map<string, boolean>();

/**
 * El índice que se le puede aplicar a UNA fila: el del catálogo cargado, y solo si es de su campaña.
 *
 * El desplegable de campaña se puede cambiar sin volver a buscar, y en ese hueco las filas en
 * pantalla son de una campaña y el catálogo recién cargado es de otra. Rotular «nuestra» con el
 * catálogo equivocado es justo el error que `es_propia` venía a eliminar —una marca de la
 * competencia contada como propia en un reporte de participación—, así que ante la duda no se rotula
 * nada hasta la próxima búsqueda.
 */
export function ladosDeFila(
  catalogo: { campaignId: string; lados: ReadonlyMap<string, boolean> }, campaignId: string,
): ReadonlyMap<string, boolean> {
  return catalogo.campaignId === campaignId ? catalogo.lados : SIN_LADOS;
}

/**
 * El rótulo del lado de una marca, o `null` cuando no se sabe de qué lado está.
 *
 * `null` es un resultado legítimo y frecuente: la marca tecleada a mano, la de una campaña cuyo
 * catálogo todavía no cargó, la que alguien dio de baja. **No rotular es la respuesta correcta ahí**
 * — `es_propia` es lo que convierte una lista de precios en participación propia contra competencia,
 * y un rótulo inventado se lee igual de firme que uno cierto.
 */
export function insigniaDeLado(
  nombre: string, indice: ReadonlyMap<string, boolean>,
): Insignia | null {
  const propia = indice.get(claveDeMarca(nombre));
  if (propia === undefined) return null;
  return propia
    ? {
      label: 'nuestra',
      titulo: 'Marca de Softys.',
      clase: 'bg-blue-100 text-blue-900',
    }
    : {
      label: 'comp.',
      titulo: 'Marca de la competencia.',
      clase: 'bg-amber-50 text-amber-900',
    };
}

// ---------------------------------------------------------------------------
// Cómo se lee un precio
// ---------------------------------------------------------------------------

/** Lo que hay que decir de un precio para que se pueda comparar con otro. */
export interface LecturaDePrecio {
  /** Qué es el número anotado: «precio del paquete de 44», «precio de una unidad». */
  base: string;
  /**
   * true = la ficha NO declaró si era paquete o unidad y se está leyendo como paquete.
   *
   * Son las 37 filas anteriores a la 0060. Se marca en pantalla porque es una lectura nuestra y no
   * lo que dijo quien estuvo en el puesto (ver `PRECIO_POR_PREDETERMINADO`): quien discuta un
   * número con el cliente tiene derecho a saber cuál de los dos está mirando.
   */
  supuesto: boolean;
  /** El precio por unidad ya formateado, o `null` si no se puede calcular. */
  porUnidad: string | null;
  /** Qué falta para poder calcularlo. Se dice en vez de dejar la celda muda. */
  faltaParaCalcular: string | null;
  /**
   * true = el precio por unidad cae fuera del rango DE SU MARCA (0062).
   *
   * Antes era una banda única de S/ 0.30–3.00 para todas las marcas, y con el material real del
   * canal —pañal a S/ 0.73 la unidad, papel higiénico grande a S/ 5.83— marcaba 81 de 143 visitas.
   * Una alerta que salta en más de la mitad de los casos deja de ser una alerta.
   */
  sospechoso: boolean;
}

/**
 * Cómo se lee una fila de precio. **No recalcula nada: usa el `precioUnitario` que ya trae la fila.**
 *
 * Es el arreglo de un error que estaba vivo en esta pantalla: pintaba
 * `precioPorUnidad(p.precioPaquete, p.unidadesPaquete)`, o sea dividía SIEMPRE, ignoraba
 * `precio_por` y no miraba las unidades de la presentación. Con eso, la fila donde la auditora
 * anotó bien S/1.00 el pañal suelto salía como S/0.018 por unidad, y toda fila con presentación del
 * catálogo y sin unidades tecleadas salía sin precio por unidad aunque el catálogo las trajera. La
 * cuenta vive en `precioUnitario` (dominio) y la resuelve la API una sola vez; acá solo se explica.
 */
export function lecturaDePrecio(p: VisitaPrecioRow): LecturaDePrecio {
  const porPaquete = (p.precioPor ?? PRECIO_POR_PREDETERMINADO) === 'paquete';
  const unidades = unidadesEfectivas(p.unidadesPaquete, p.unidadesPresentacion);
  const base = !porPaquete
    ? 'precio de una unidad'
    : unidades !== null
      ? `precio del paquete de ${unidades}`
      : 'precio del paquete';

  // El orden importa: un precio que no es un número mayor que cero no se puede dividir por nada, y
  // culpar a las unidades ahí mandaría a corregir el campo equivocado.
  let falta: string | null = null;
  if (p.precioUnitario === null) {
    falta = !Number.isFinite(p.precioPaquete) || p.precioPaquete <= 0
      ? 'el precio anotado no es un número mayor que cero'
      : 'no dice cuántas unidades trae el paquete';
  }

  return {
    base,
    supuesto: p.precioPor === null,
    porUnidad: p.precioUnitario === null ? null : `S/ ${p.precioUnitario.toFixed(2)}`,
    faltaParaCalcular: falta,
    sospechoso: ppuFueraDeRango(p.precioUnitario, p),
  };
}

// ---------------------------------------------------------------------------
// Hallazgos: una marca que el auditor no conocía
// ---------------------------------------------------------------------------

/**
 * En qué quedó un hallazgo. Los tres estados de `levantamiento_hallazgos`.
 *
 * Antes se pintaba el valor crudo de la columna —«pendiente», «confirmado», «descartado», tal cual—
 * y solo cuando NO se podía accionar; ahora se ve siempre, porque «esto ya se revisó y se descartó»
 * es tanto una respuesta como «esto está sin revisar».
 */
const HALLAZGO_INSIGNIA: Record<string, Insignia> = {
  pendiente: {
    label: 'Por confirmar',
    titulo: 'Nadie decidió todavía si es una marca de verdad.',
    clase: 'bg-purple-200 text-purple-900',
  },
  confirmado: {
    label: 'Es marca',
    titulo: 'Se confirmó: es una marca real y entra al catálogo.',
    clase: 'bg-emerald-100 text-emerald-900',
  },
  descartado: {
    label: 'Descartado',
    titulo: 'Se revisó y no era una marca: un empaque mal leído, una variedad de otra marca.',
    clase: 'bg-gray-100 text-gray-600',
  },
};

export function insigniaDeHallazgo(estado: string): Insignia {
  return HALLAZGO_INSIGNIA[estado] ?? {
    label: estado,
    titulo: 'Esta pantalla no conoce ese estado. Está tal como lo guardó el servidor.',
    clase: 'bg-yellow-100 text-yellow-900',
  };
}

// ---------------------------------------------------------------------------
// El filtro por categoría de producto
// ---------------------------------------------------------------------------

/**
 * Las visitas de una categoría. Cadena vacía = todas.
 *
 * FILTRA EN MEMORIA, sobre lo ya buscado, y no en la consulta: `FiltrosBandejaVisitas` no tiene
 * categoría —`categorias` es una columna `text[]` y filtrarla en PostgREST pide un operador de
 * contención que la API no expone hoy—. Filtrar acá tiene además una ventaja que conviene no perder
 * al «mejorarlo»: cambiar de categoría no vuelve a consultar, así que comparar papel contra pañales
 * en una reunión es instantáneo y no depende de la red.
 *
 * ⚠️ Una visita **anterior a la 0060 no tiene categorías** y por lo tanto no entra en NINGUNA. Son
 * las 18 visitas reales del 19-ago y la de abarrotes. Por eso la pantalla, cuando el filtro está
 * puesto, dice cuántas quedaron fuera por eso (`medidasSinCategoria`): un filtro que esconde datos
 * sin decirlo se lee como que esos datos no existen.
 */
export function filtrarPorCategoria(
  filas: readonly VisitaRow[], categoria: CategoriaProducto | '',
): VisitaRow[] {
  if (categoria === '') return [...filas];
  return filas.filter((v) => v.categorias.includes(categoria));
}

/**
 * Cuántas visitas MEDIDAS no declararon ninguna categoría.
 *
 * Solo las medidas: una «no pude» no tiene categorías por diseño —no se llegó a preguntar qué vende
 * el puesto— y contarla acá haría creer que hay un hueco de dato donde no lo hay.
 */
export function medidasSinCategoria(filas: readonly VisitaRow[]): number {
  return filas.filter((v) => v.estado === 'medido' && v.categorias.length === 0).length;
}

// ---------------------------------------------------------------------------
// El resumen de arriba
// ---------------------------------------------------------------------------

export interface ResumenBandeja {
  visitas: number;
  medidas: number;
  noPude: number;
  anuladas: number;
  conBandera: number;
  /** Cuántas mediciones «se le acabó» hay en total, no cuántas visitas tienen alguna. */
  quiebres: number;
  hallazgosPendientes: number;
}

/**
 * Los números del encabezado, en una sola pasada y fuera del componente para poder verificarlos.
 *
 * QUÉ CUENTA CADA UNO, porque no todos tratan igual a las anuladas y eso hay que poder leerlo:
 * `medidas` y `noPude` van por estado, así que una anulada no cae en ninguna de las dos —anular
 * cambia el estado— y por eso `visitas` puede ser mayor que su suma. `conBandera` SÍ incluye
 * anuladas: la casilla «solo con banderas» también las muestra, y que el número del resumen y el de
 * la tabla no coincidan es peor que contar una anulada.
 *
 * `quiebres` excluye las anuladas a propósito y es el único que lo hace: es un número para ACCIONAR
 * —un pedido al distribuidor—, y una medición anulada es justamente la que ya se dijo que no cuenta
 * para nadie. Salir a resolver el quiebre de un puesto cuya visita se anuló es trabajo perdido.
 */
export function resumenDeBandeja(filas: readonly VisitaRow[]): ResumenBandeja {
  let medidas = 0; let noPude = 0; let anuladas = 0; let conBandera = 0;
  let quiebres = 0; let hallazgosPendientes = 0;
  for (const v of filas) {
    if (v.estado === 'medido') medidas += 1;
    if (v.estado === 'no_pude') noPude += 1;
    if (v.estado === 'anulado') anuladas += 1;
    if (v.flags.length > 0) conBandera += 1;
    if (v.estado !== 'anulado') {
      quiebres += v.mediciones.filter((m) => m.presencia === 'quiebre').length;
    }
    hallazgosPendientes += v.hallazgos.filter((h) => h.estado === 'pendiente').length;
  }
  return {
    visitas: filas.length, medidas, noPude, anuladas, conBandera, quiebres, hallazgosPendientes,
  };
}

// ---------------------------------------------------------------------------
// El contacto del puestero — dato personal de un tercero
// ---------------------------------------------------------------------------

/**
 * En qué estado está el contacto de una fila. **Nace sin pedirse, y esa es la regla.**
 *
 * ⚠️ POR QUÉ NO SE MUESTRA SOLO. El nombre y el teléfono son de una persona que **no es usuaria del
 * sistema y no aceptó nada**: dejó que le midieran el puesto. La 0060 les revocó el SELECT a
 * `authenticated` y los dejó detrás de `get_contactos_visitas`, que comprueba el permiso y escribe
 * una fila en `audit_log` por cada lectura. Pintarlos en la tabla convertiría «abrir la bandeja para
 * revisar precios» —que es el 99% de las veces que se abre— en una lectura masiva de teléfonos, con
 * su rastro de auditoría y su copia en la memoria del navegador, sin que nadie los haya pedido.
 *
 * Por eso la bandeja **no manda `incluirContacto: true`** en la búsqueda: los pide de a UNA visita,
 * cuando alguien pulsa «Contacto» en esa fila. Una fila de auditoría por teléfono realmente mirado
 * es una respuesta útil a «quién vio el teléfono del puestero»; una por cada vez que se abrió la
 * pantalla no es ninguna.
 */
export type ContactoVisible =
  | { estado: 'cargando' }
  | { estado: 'visible'; nombre: string | null; telefono: string | null }
  | { estado: 'sin_dato' }
  | { estado: 'no_disponible' }
  | { estado: 'error'; mensaje: string };

/**
 * Qué mostrar a partir de lo que devolvió `getContactosVisitas`.
 *
 * LA DISTINCIÓN QUE ESTO EXISTE PARA HACER: la RPC devuelve una fila por cada visita que quien
 * pregunta PUEDE ver, aunque venga vacía, y **ninguna** por las que no. Entonces «no vino en el
 * mapa» y «vino con los dos campos en blanco» significan cosas distintas —no se pudo leer, contra el
 * puestero no dejó datos— y fundirlas en un «—» dejaría al coordinador buscando un teléfono que
 * nunca existió, o creyendo que no existe uno que sí está pero él no puede ver.
 */
export function contactoDesdeMapa(
  visitaId: string,
  mapa: ReadonlyMap<string, { nombre: string | null; telefono: string | null }>,
): ContactoVisible {
  const c = mapa.get(visitaId);
  if (c === undefined) return { estado: 'no_disponible' };
  if ((c.nombre ?? '').trim() === '' && (c.telefono ?? '').trim() === '') {
    return { estado: 'sin_dato' };
  }
  return { estado: 'visible', nombre: c.nombre, telefono: c.telefono };
}

function Modal(
  { titulo, onCerrar, ancho, children }:
  { titulo: string; onCerrar: () => void; ancho?: 'ancho'; children: ReactNode },
) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className={`max-h-[90vh] w-full overflow-y-auto rounded bg-white p-5 shadow-lg ${
        ancho === 'ancho' ? 'max-w-2xl' : 'max-w-lg'}`}>
        <div className="mb-3 flex items-start justify-between">
          <h2 className="text-lg font-semibold">{titulo}</h2>
          <button type="button" onClick={onCerrar} aria-label="Cerrar"
            className="text-gray-400 hover:text-gray-700">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Corregir una visita (0059). Todo lo de acá es PURO y exportado a propósito: son las reglas que
// deciden qué viaja al servidor y quién puede accionar, y una regla que solo vive dentro de un
// componente no se puede verificar sin montar la pantalla entera.
// ---------------------------------------------------------------------------

/**
 * ¿Se le ofrecen corregir y anular ESTA fila? Espejo de `puede_editar_visita` (0059).
 *
 * El jefe de operaciones puede con todo; el coordinador, solo con las campañas donde es el
 * designado. **El rol no alcanza**: `coordinador` lo comparten el coordinador y los supervisores de
 * la campaña, así que decidir por rol le mostraría a un supervisor botones que el servidor rechaza
 * —"un botón que el servidor va a rechazar es una trampa" (BITACORA §2)—.
 *
 * `esValidador` (rol `datos`, 0061) entra por la puerta contraria, y por eso hay que nombrarlo
 * aparte: el servidor YA lo autoriza —`puede_editar_visita` empieza con `has_role('admin') or
 * has_role('datos')`— pero esta función solo miraba admin y campañas coordinadas, y el validador no
 * coordina ninguna (ve todas por su rol, que es justo el punto de la 0061). El conjunto le quedaba
 * vacío y **los botones no se dibujaban nunca**: el usuario lo reportó el 2026-08-22 como «no le
 * permitía editar». Es el fallo inverso al que evita la regla de arriba —el servidor deja y la
 * pantalla no ofrece— y se ve peor todavía, porque no hay error que leer: simplemente no hay botón.
 *
 * Es el mismo predicado que `puedeAccionarFila` en la bandeja de canjes, y se escribe aparte a
 * propósito: son dos permisos distintos en el servidor (`puede_editar_canje` y
 * `puede_editar_visita`) y compartir la función acá haría creer que aflojar uno afloja el otro.
 */
export function puedeAccionarFilaVisita(
  esAdmin: boolean, esValidador: boolean,
  campaniasCoordinadas: ReadonlySet<string>, campaignId: string,
): boolean {
  return esAdmin || esValidador || campaniasCoordinadas.has(campaignId);
}

/**
 * Lo que el modal de corrección tiene en pantalla.
 *
 * Todo es TEXTO, incluso el número del puesto y el booleano de «no quiso responder»: es lo que
 * escriben los controles del navegador y es la forma en que viaja a la RPC, que castea al tipo de
 * la columna del lado del servidor. Convertir acá agregaría un segundo lugar donde equivocarse.
 *
 * **Vacío NO significa «no se toca ese campo»**: eso lo dice el conjunto de campos MARCADOS, que va
 * aparte. Acá solo viven los valores. Es la misma lección del modal de canjes (0057).
 */
export interface FormCorreccionVisita {
  fecha: string;
  numeroRecorrido: string;
  nombrePuesto: string;
  pdvId: string;
  mercadoLibre: string;
  auditorDeclarado: string;
  giro: string;
  clientePrincipal: string;
  motivoNoPude: string;
  marcaRecomendada: string;
  motivoRecomendacion: string;
  /** `'true'` / `'false'`. Es un select, así que nunca puede quedar vacío. */
  recomendacionDeclinada: string;
  obsPuesto: string;
  obsGiro: string;
  obsCliente: string;
  obsCuadricula: string;
  obsPrecios: string;
  obsPromocion: string;
  obsRecomendacion: string;
  obsEvidencia: string;
}

export type CampoCorreccionVisita = keyof FormCorreccionVisita;

/**
 * De cada campo del formulario, la COLUMNA que espera la lista blanca de `corregir_visita`.
 *
 * Es la única traducción entre los dos vocabularios, y por eso es también la lista de qué se puede
 * corregir desde esta pantalla: un campo que no esté acá no viaja, y uno que esté mal escrito lo
 * rechaza el servidor con «Campo no corregible», que es un error ruidoso y no una corrupción.
 *
 * `ocurrido_en` está en la lista blanca del servidor y NO acá: esta pantalla nunca muestra el
 * momento declarado —el módulo trabaja con `fecha` y `registrado_en`—, así que ofrecer un control
 * para corregirlo sería pedirle a alguien que edite un dato que no puede ver.
 */
export const COLUMNA_DE_CAMPO: Record<CampoCorreccionVisita, string> = {
  fecha: 'fecha',
  numeroRecorrido: 'numero_recorrido',
  nombrePuesto: 'nombre_puesto',
  pdvId: 'pdv_id',
  mercadoLibre: 'mercado_libre',
  auditorDeclarado: 'auditor_declarado',
  giro: 'giro',
  clientePrincipal: 'cliente_principal',
  motivoNoPude: 'motivo_no_pude',
  marcaRecomendada: 'marca_recomendada',
  motivoRecomendacion: 'motivo_recomendacion',
  recomendacionDeclinada: 'recomendacion_declinada',
  ...(Object.fromEntries(
    SECCIONES_OBSERVABLES.map((k) => [k, COLUMNA_DE_SECCION[k]]),
  ) as Record<SeccionObservable, string>),
};

/** Los campos, en el orden del formulario. Sale del mapa para que no se puedan desincronizar. */
const CAMPOS_CORREGIBLES = Object.keys(COLUMNA_DE_CAMPO) as CampoCorreccionVisita[];

/**
 * Cómo se llama cada campo EN PANTALLA.
 *
 * Un solo mapa para la etiqueta del control y para los avisos que hablan de él: un aviso que diga
 * «Fecha está marcada y vacía» mientras el control se rotula de otra forma manda a buscar un campo
 * que no existe con ese nombre.
 */
export const ETIQUETA_CAMPO_VISITA: Record<CampoCorreccionVisita, string> = {
  fecha: 'Fecha',
  numeroRecorrido: 'N.º del puesto',
  nombrePuesto: 'Nombre del puesto',
  pdvId: 'Mercado de la lista',
  mercadoLibre: 'Mercado escrito a mano',
  auditorDeclarado: 'Auditor declarado',
  giro: '¿Qué vende?',
  clientePrincipal: '¿A quién le vende?',
  motivoNoPude: 'Motivo de «no pude»',
  marcaRecomendada: 'Marca que recomienda',
  motivoRecomendacion: 'Por qué la recomienda',
  recomendacionDeclinada: '¿No quiso responder?',
  ...(Object.fromEntries(
    SECCIONES_OBSERVABLES.map((k) => [k, `Observación · ${TITULO_DE_SECCION[k]}`]),
  ) as Record<SeccionObservable, string>),
};

/**
 * Nombres legibles de las COLUMNAS, para el historial.
 *
 * El historial trae los nombres tal como los guardó el servidor (`numero_recorrido`), que es el
 * nombre de la columna y no algo que el jefe de operaciones tenga por qué reconocer. `estado` no
 * está en `COLUMNA_DE_CAMPO` —no se corrige, se anula— pero sí aparece en el rastro, porque la
 * anulación deja su propia fila (0059).
 */
export const CAMPO_VISITA_LABEL: Record<string, string> = {
  ...(Object.fromEntries(
    CAMPOS_CORREGIBLES.map((c) => [COLUMNA_DE_CAMPO[c], ETIQUETA_CAMPO_VISITA[c]]),
  ) as Record<string, string>),
  ocurrido_en: 'Momento declarado',
  estado: 'Estado',
};

/**
 * Campos que la base declara NOT NULL: se pueden CAMBIAR pero no vaciar.
 *
 * `fecha` y `numero_recorrido` nacen `not null` en la 0047 y nadie los aflojó (`pdv_id` sí, en la
 * 0056, y por eso NO está acá). `recomendacionDeclinada` también es NOT NULL, pero su control es un
 * select de dos opciones y no puede quedar vacío: igual se lista, para que la regla no dependa de
 * qué control se eligió pintar.
 *
 * Mandarlos vacíos haría que el servidor rechace la corrección ENTERA —incluidos los otros campos
 * de la misma tanda—, así que se filtran acá y la pantalla lo dice al lado del control.
 */
const NO_VACIABLES: readonly CampoCorreccionVisita[] = [
  'fecha', 'numeroRecorrido', 'recomendacionDeclinada',
];

/**
 * ¿Marcar este campo y dejarlo en blanco borra el dato, o el servidor lo va a rechazar?
 *
 * Se exporta para que la pantalla y el diff lean la MISMA lista: si un día se afloja un NOT NULL
 * —ya pasó con `pdv_id` en la 0056— se corrige en un solo sitio, en vez de que el aviso de la
 * pantalla siga diciendo "obligatorio" para siempre.
 */
export function sePuedeVaciarVisita(campo: CampoCorreccionVisita): boolean {
  return !NO_VACIABLES.includes(campo);
}

/** El texto de una observación de la visita, o cadena vacía si esa sección no tiene ninguna. */
function textoDeObservacion(v: VisitaRow, columna: string): string {
  return v.observaciones.find((o) => o.campo === columna)?.texto ?? '';
}

/** El formulario tal como está HOY el registro. Es lo que se precarga y lo contra lo que se difea. */
export function formVisitaDesde(v: VisitaRow): FormCorreccionVisita {
  const obs = Object.fromEntries(
    SECCIONES_OBSERVABLES.map((k) => [k, textoDeObservacion(v, COLUMNA_DE_SECCION[k])]),
  ) as Record<SeccionObservable, string>;
  return {
    fecha: v.fecha,
    numeroRecorrido: String(v.numeroRecorrido),
    nombrePuesto: v.nombrePuesto ?? '',
    pdvId: v.pdv?.id ?? '',
    mercadoLibre: v.mercadoLibre ?? '',
    auditorDeclarado: v.auditorDeclarado ?? '',
    giro: v.giro ?? '',
    clientePrincipal: v.clientePrincipal ?? '',
    motivoNoPude: v.motivoNoPude ?? '',
    marcaRecomendada: v.marcaRecomendada ?? '',
    motivoRecomendacion: v.motivoRecomendacion ?? '',
    recomendacionDeclinada: v.recomendacionDeclinada ? 'true' : 'false',
    ...obs,
  };
}

/**
 * El formulario con UN campo repuesto desde el registro. Es lo que pasa al DESMARCAR su casilla.
 *
 * No es cosmética: la pantalla promete que los campos sin marcar «quedan deshabilitados mostrando
 * lo que dice hoy el registro». Sin reponer, el control apagado se queda mostrando lo tecleado, que
 * no es ni lo que dice el registro ni lo que se va a guardar. Y acá tiene una segunda consecuencia:
 * `bloqueoDeCorreccionVisita` mira el formulario para saber cómo quedaría la visita, y eso solo es
 * cierto mientras los campos sin marcar contengan el valor original.
 */
export function reponerCampoVisita(
  v: VisitaRow, f: FormCorreccionVisita, campo: CampoCorreccionVisita,
): FormCorreccionVisita {
  return { ...f, [campo]: formVisitaDesde(v)[campo] };
}

/**
 * El diff que viaja a `corregir_visita`: solo los campos MARCADOS y que además cambiaron.
 *
 * Las dos condiciones hacen falta y ninguna sobra:
 *
 * - **Marcado** es lo que evita que un control precargado que alguien rozó sin querer viaje igual.
 *   El formulario nace con los veinte valores puestos, así que sin la casilla «no lo toqué» y «lo
 *   dejé como estaba» son indistinguibles.
 * - **Cambió** es lo que mantiene el historial legible. Marcar un campo y dejarlo igual no es una
 *   corrección, y guardarlo llenaría `levantamiento_correcciones` de filas «X → X» que ensucian
 *   justamente el rastro que hace legítimo corregir una medición de campo.
 *
 * Se RECORTA antes de comparar y antes de mandar: un espacio al borde no es una corrección, y en
 * `mercado_libre` sería peor que ruido —el índice del cierre de jornada normaliza con
 * `lower(btrim(...))` (0056), así que «Naranjal » y «Naranjal» serían el mismo mercado allá y dos
 * textos distintos acá—.
 *
 * Un campo marcado y dejado VACÍO sí viaja, como cadena vacía: es la forma de borrar un dato, y el
 * servidor la traduce a NULL. Se excluyen los NOT NULL (ver `NO_VACIABLES`), que rebotarían la
 * tanda entera.
 */
export function cambiosDeVisita(
  v: VisitaRow, f: FormCorreccionVisita, marcados: ReadonlySet<CampoCorreccionVisita>,
): Record<string, string> {
  const original = formVisitaDesde(v);
  const out: Record<string, string> = {};
  for (const campo of CAMPOS_CORREGIBLES) {
    if (!marcados.has(campo)) continue;
    const nuevo = f[campo].trim();
    if (nuevo === original[campo].trim()) continue;
    if (nuevo === '' && !sePuedeVaciarVisita(campo)) continue;
    out[COLUMNA_DE_CAMPO[campo]] = nuevo;
  }
  return out;
}

/**
 * Por qué NO se puede guardar todavía, o `null` si sí se puede.
 *
 * Devuelve el TEXTO en vez de un booleano para poder ponerlo a la vista junto al botón apagado: un
 * botón que se puede pulsar y recién entonces avisa deja la duda de si el guardado entró a medias,
 * que en una pantalla de corrección es la peor duda posible.
 *
 * Los tres bloqueos que miran el formulario adivinan lo que el servidor va a contestar, y existen
 * para que la respuesta llegue ANTES: un rechazo del servidor aborta la corrección entera, así que
 * el coordinador perdería también los campos que sí estaban bien.
 *
 * `form` y `estado` son opcionales solo para poder llamarla sin modal abierto; con el modal abierto
 * SIEMPRE se pasan.
 */
export function bloqueoDeCorreccionVisita(
  marcados: ReadonlySet<CampoCorreccionVisita>,
  motivo: string,
  form?: FormCorreccionVisita,
  estado?: VisitaRow['estado'],
): string | null {
  if (marcados.size === 0) return 'Marca al menos un campo para cambiar.';

  if (form !== undefined) {
    // 1. Un obligatorio marcado y en blanco. Callarlo era peor que rechazarlo: `cambiosDeVisita` lo
    //    descarta y el guardado seguiría con los demás campos diciendo «Corrección guardada» —o
    //    sea, se anunciaría como hecho justo lo que se ignoró.
    const vacio = NO_VACIABLES.find((c) => marcados.has(c) && form[c].trim() === '');
    if (vacio !== undefined) {
      return `Marcaste «${ETIQUETA_CAMPO_VISITA[vacio]}» y la dejaste vacía, y es obligatoria: `
        + 'elige un valor o desmarca la casilla.';
    }

    // 2. La visita se quedaría sin mercado. El CHECK `levantamiento_visita_tiene_mercado` (0056)
    //    exige uno de los dos, y este es el error fácil de cometer: quitar el mercado de la lista
    //    para escribirlo a mano y olvidarse de escribirlo.
    if (form.pdvId.trim() === '' && form.mercadoLibre.trim() === '') {
      return 'La visita se quedaría sin mercado: elige uno de la lista o escribe uno a mano.';
    }

    // 3. Una visita MEDIDA no puede quedarse sin giro ni sin cliente principal: lo impide el CHECK
    //    `levantamiento_visita_completa`. Las otras tres condiciones de ese CHECK —promoción, marca
    //    desconocida y foto— no son corregibles desde acá, así que no pueden romperse.
    if (estado === 'medido') {
      const falta = (['giro', 'clientePrincipal'] as CampoCorreccionVisita[])
        .find((c) => form[c].trim() === '');
      if (falta !== undefined) {
        return `Una visita medida necesita «${ETIQUETA_CAMPO_VISITA[falta]}». Si el puesto no se `
          + 'pudo medir, la salida es anular la visita, no vaciarle los campos.';
      }
    }
  }

  if (motivo.trim() === '') return 'Escribe el motivo de la corrección.';
  return null;
}

/**
 * Los enlaces a las fotos de UN campo (el puesto, un precio, una promoción, un hallazgo).
 *
 * Desde la 0058 cada campo guarda un arreglo de rutas, así que pintar `foto` a secas escondería
 * todas menos la primera: el auditor tomó tres fotos del precio y el panel mostraría una, que es
 * justo la evidencia que el cliente discute. Van NUMERADAS —«foto 1», «foto 2»— para que se vea
 * cuántas hay antes de abrirlas; con una sola el número sobra y estorba.
 *
 * Con cero fotos no se pinta un enlace muerto: se pinta `vacio`, que casi siempre es nada. Un
 * botón que abre una ruta inexistente termina en «no se pudo abrir la foto» y deja al coordinador
 * creyendo que la evidencia se perdió, cuando nunca existió.
 *
 * Está declarado acá arriba y no dentro de la bandeja a propósito: un componente declarado dentro
 * de otro se recrea en cada render y React desmonta y vuelve a montar su subárbol entero.
 */
function FotosDeCampo(
  { paths, onVer, className, vacio = null }:
  { paths: string[]; onVer: (path: string) => void; className: string; vacio?: ReactNode },
) {
  if (paths.length === 0) return <>{vacio}</>;
  return (
    <>
      {paths.map((p, i) => (
        <button key={`${i}-${p}`} type="button" onClick={() => onVer(p)} className={className}>
          {paths.length === 1 ? 'foto' : `foto ${i + 1}`}
        </button>
      ))}
    </>
  );
}

/**
 * Un rótulo de color con su explicación al posar el cursor.
 *
 * Todos los de esta pantalla salen de acá para que no se separen: el mismo verde tiene que querer
 * decir lo mismo en la cuadrícula y en los hallazgos, y con la clase escrita en cada sitio donde se
 * pinta eso dura hasta el primer retoque.
 */
function Rotulo({ insignia }: { insignia: Insignia }) {
  return (
    <span title={insignia.titulo}
      className={`whitespace-nowrap rounded px-1 py-0.5 text-xs ${insignia.clase}`}>
      {insignia.label}
    </span>
  );
}

/** Una marca de la cuadrícula: de qué lado está, cómo se llama y qué pasa con ella hoy. */
function LineaMedicion(
  { m, lado }: { m: VisitaMedicionRow; lado: Insignia | null },
) {
  return (
    <div className="flex flex-wrap items-center gap-1">
      {lado !== null && <Rotulo insignia={lado} />}
      <span className="text-xs text-gray-700">{m.marcaNombre}</span>
      <Rotulo insignia={insigniaDePresencia(m.presencia)} />
    </div>
  );
}

/**
 * Una fila de precio: lo que anotó la auditora, qué significa ese número y cuánto sale la unidad.
 *
 * El precio por unidad va en negrita y al final porque **es la cifra que se lleva a la reunión**:
 * Elite XL x4 a S/7.00 contra Suave x4 a S/4.00 no se pueden comparar hasta dividirlos, y hasta hoy
 * el panel mostraba esa división mal calculada (ver `lecturaDePrecio`) o no la mostraba.
 */
function LineaPrecio(
  { p, lado, onVer }:
  { p: VisitaPrecioRow; lado: Insignia | null; onVer: (path: string) => void },
) {
  const l = lecturaDePrecio(p);
  return (
    <div className="mb-1.5 text-xs last:mb-0">
      <div className="flex flex-wrap items-center gap-1">
        {lado !== null && <Rotulo insignia={lado} />}
        <span className="text-gray-700">{p.marcaNombre}</span>
        {p.presentacion !== null && p.presentacion.trim() !== '' && (
          <span className="text-gray-400">· {p.presentacion}</span>
        )}
      </div>
      <div className="flex flex-wrap items-baseline gap-1">
        <span>S/ {p.precioPaquete.toFixed(2)}</span>
        {/* Qué significa ese número. Sin esto, S/44.00 y S/1.00 se leen como el mismo tipo de
            dato, que es exactamente la confusión que la 0060 vino a cerrar. */}
        <span className={`text-gray-500 ${l.supuesto ? 'italic' : ''}`}
          title={l.supuesto
            ? 'La ficha no declaró si era el precio del paquete o el de una unidad: es anterior al '
              + 'rediseño. Se lee como paquete, que es lo que significaba la columna.'
            : undefined}>
          ({l.base}{l.supuesto ? ', supuesto' : ''})
        </span>
        {l.porUnidad !== null ? (
          <strong className={l.sospechoso ? 'text-red-700' : 'text-gray-900'}
            title={l.sospechoso
              ? 'Cae fuera de S/ 0.30–3.00 por unidad. Casi siempre es el precio del paquete '
                + 'anotado como si fuera el de una unidad, o al revés.'
              : undefined}>
            = {l.porUnidad}/u
          </strong>
        ) : (
          <span className="text-gray-400">
            sin precio por unidad{l.faltaParaCalcular !== null ? `: ${l.faltaParaCalcular}` : ''}
          </span>
        )}
        <FotosDeCampo paths={p.fotoPaths} onVer={onVer} className="text-blue-600 underline" />
      </div>
    </div>
  );
}

/**
 * Un hallazgo: la marca que el auditor no reconoció, con su foto y en qué quedó.
 *
 * La FOTO se muestra siempre, y antes solo aparecía en los pendientes que además se podían
 * accionar: es la única prueba de qué se vio, y esconderla en los ya resueltos dejaba sin poder
 * revisar justo la decisión que alguien tomó —«¿por qué se descartó esto?»— cuando aparece la misma
 * marca en otro mercado.
 */
function LineaHallazgo(
  { h, puedeResolver, onVer, onResolver }: {
    h: VisitaHallazgoRow;
    puedeResolver: boolean;
    onVer: (path: string) => void;
    onResolver: (id: string, estado: 'confirmado' | 'descartado') => void;
  },
) {
  return (
    <div className="mb-1 rounded border border-purple-200 bg-purple-50 px-1.5 py-1 last:mb-0">
      <div className="text-xs font-medium text-purple-900">{h.nombreLeido}</div>
      <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
        <Rotulo insignia={insigniaDeHallazgo(h.estado)} />
        <FotosDeCampo paths={h.fotoPaths} onVer={onVer}
          className="text-xs text-blue-600 underline"
          vacio={<span className="text-xs text-gray-400">sin foto</span>} />
      </div>
      {h.estado === 'pendiente' && puedeResolver && (
        <div className="mt-1 flex gap-2 text-xs">
          <button type="button" onClick={() => onResolver(h.id, 'confirmado')}
            className="text-green-700 underline">es marca</button>
          <button type="button" onClick={() => onResolver(h.id, 'descartado')}
            className="text-gray-600 underline">descartar</button>
        </div>
      )}
    </div>
  );
}

/**
 * El contacto del puestero: se pide de a uno y con un toque, nunca se pinta solo.
 *
 * El porqué entero está en `ContactoVisible`. Acá lo que importa es que el botón solo se le ofrece a
 * quien PUEDE accionar esa fila —el mismo predicado que corregir y anular, que es el que
 * `get_contactos_visitas` comprueba del lado del servidor—: a los demás la RPC les devolvería vacío,
 * y un botón que siempre contesta «no disponible» enseña a desconfiar de la pantalla.
 */
function BloqueContacto(
  { estado, onPedir, onOcultar }: {
    estado: ContactoVisible | undefined;
    onPedir: () => void;
    onOcultar: () => void;
  },
) {
  if (estado === undefined) {
    return (
      <button type="button" onClick={onPedir}
        title="Dato personal del puestero. Pedirlo queda registrado en la auditoría."
        className="block text-xs text-blue-600 underline">Contacto</button>
    );
  }
  if (estado.estado === 'cargando') {
    return <span className="block text-xs text-gray-500">Contacto…</span>;
  }
  return (
    // `whitespace-normal` porque la celda de acciones va con `whitespace-nowrap`: sin esto, «María
    // del Carmen Quispe» ensancharía la última columna hasta empujar la tabla fuera de la vista.
    <div className="mt-1 max-w-[14rem] whitespace-normal break-words rounded border
      border-gray-200 bg-gray-50 px-1.5 py-1">
      {estado.estado === 'visible' && (
        <>
          <div className="text-xs text-gray-800">{estado.nombre ?? 'sin nombre'}</div>
          <div className="text-xs text-gray-800">{estado.telefono ?? 'sin teléfono'}</div>
        </>
      )}
      {estado.estado === 'sin_dato' && (
        <div className="text-xs text-gray-500">Este puesto no dejó contacto.</div>
      )}
      {estado.estado === 'no_disponible' && (
        <div className="text-xs text-gray-500">
          No se pudo leer el contacto de esta visita.
        </div>
      )}
      {estado.estado === 'error' && (
        <div className="text-xs text-red-700">{estado.mensaje}</div>
      )}
      <button type="button" onClick={onOcultar}
        className="text-xs text-blue-600 underline">Ocultar</button>
    </div>
  );
}

/**
 * Bandeja del levantamiento de canal: lo que el campo midió, por rango de fechas.
 *
 * **Sí se corrige, desde la 0059, y eso cambió respecto de como nació el módulo.** La 0047 decía
 * que «los hechos no se editan» y no existía `corregir_visita`; el primer día de uso real mostró
 * que ese razonamiento vale para lo OBSERVADO —la cuadrícula, los precios, las promociones y los
 * hallazgos, que siguen entrando solo por `registrar_visita`— y no para la cabecera que ubica esa
 * observación. Las 18 visitas reales del 19-ago quedaron archivadas en la campaña equivocada, y con
 * «volver a medir» como única salida había que mandar gente otra vez a dos mercados.
 *
 * Lo que hace que corregir no vuelva a un dato indistinguible de uno inventado es el RASTRO: valor
 * anterior, motivo, autor y fecha por cada campo, visible en «Historial».
 */
export default function LevantamientoBandeja() {
  const { roles } = useAuth();
  /** Puerta de la PANTALLA. La de cada fila es `puedeAccionarFilaVisita`, que sí mira la campaña. */
  const puedeAccionar = puedeEditar('levantamiento', roles);
  const esAdmin = roles.includes('admin');
  /** El validador de datos (0061): acciona en TODAS las campañas, igual que el servidor le permite. */
  const esValidador = roles.includes('datos');
  const [campaniasCoordinadas, setCampaniasCoordinadas] = useState<Set<string>>(new Set());

  const hoy = formatInTimeZone(new Date(), TZ, 'yyyy-MM-dd');
  const inicioDeMes = hoy.slice(0, 8) + '01';

  const [campanias, setCampanias] = useState<CampaniaLevantamiento[]>([]);
  const [campaignId, setCampaignId] = useState('');
  const [mercados, setMercados] = useState<MercadoLevantamiento[]>([]);
  const [desde, setDesde] = useState(inicioDeMes);
  const [hasta, setHasta] = useState(hoy);
  const [pdvId, setPdvId] = useState('');
  const [workerId, setWorkerId] = useState('');
  const [estado, setEstado] = useState<'' | 'medido' | 'no_pude'>('');
  const [soloConBanderas, setSoloConBanderas] = useState(false);
  /**
   * La categoría de producto. **No entra en la búsqueda: filtra lo ya traído** (ver
   * `filtrarPorCategoria`), así que no necesita —ni ofrece— volver a pulsar «Buscar».
   */
  const [categoria, setCategoria] = useState<CategoriaProducto | ''>('');

  const [filas, setFilas] = useState<VisitaRow[]>([]);
  const [buscado, setBuscado] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [anulando, setAnulando] = useState<VisitaRow | null>(null);
  const [motivo, setMotivo] = useState('');

  const [corrigiendo, setCorrigiendo] = useState<VisitaRow | null>(null);
  const [form, setForm] = useState<FormCorreccionVisita | null>(null);
  /** Qué campos se van a cambiar. Arranca VACÍO: nada se toca hasta que alguien lo marque. */
  const [camposMarcados, setCamposMarcados] = useState<Set<CampoCorreccionVisita>>(new Set());
  const [historial, setHistorial] =
    useState<{ fila: VisitaRow; items: CorreccionVisitaRow[] } | null>(null);

  /**
   * De qué lado está cada marca, y DE QUÉ CAMPAÑA salió ese catálogo.
   *
   * El id viaja pegado al índice y no suelto porque las dos cosas solo sirven juntas: quién lo usa
   * tiene que comprobar que la fila que va a rotular es de esa campaña. Eso lo hace `ladosDeFila`,
   * que explica por qué.
   */
  const [catalogo, setCatalogo] =
    useState<{ campaignId: string; lados: Map<string, boolean> }>({
      campaignId: '', lados: new Map(),
    });

  /**
   * Los contactos que alguien pidió explícitamente, por id de visita. Vacío al abrir y al buscar.
   *
   * Se guardan solo mientras dura la pantalla y para las filas donde se pulsó el botón: ver
   * `ContactoVisible` para el porqué de todo esto.
   */
  const [contactos, setContactos] = useState<Map<string, ContactoVisible>>(new Map());

  // Al de oficina SÍ se le muestran los bancos de pruebas, rotulados: el banco existe para ensayar
  // el formulario y para mostrárselo a un cliente. Al auditor no (ver `campaniasParaElegir`).
  useEffect(() => {
    void listCampaniasLevantamiento(roles)
      .then((cs) => { setCampanias(cs); if (cs.length === 1) setCampaignId(cs[0].id); })
      .catch((e: unknown) => setMensaje('No se pudieron leer las campañas: ' + String(e)));
    // Ni el admin ni el validador necesitan la lista: pueden con todas las campañas, así que
    // pedirla sería una consulta al aire.
    if (!esAdmin && !esValidador) void campaniasQueCoordino().then(setCampaniasCoordinadas);
    // `roles.join(',')` y no `roles`: el arreglo es nuevo en cada render y esto correría sin parar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esAdmin, esValidador, roles.join(',')]);

  useEffect(() => {
    if (campaignId === '') { setMercados([]); return; }
    void listMercadosLevantamiento(campaignId).then(setMercados).catch(() => setMercados([]));
  }, [campaignId]);

  /**
   * El catálogo de marcas de la campaña, para saber cuál es nuestra y cuál de la competencia.
   *
   * Se pide SIN filtro de categoría —todas las de la campaña— porque las filas en pantalla pueden
   * ser de cualquiera y el filtro de categoría de esta pantalla se aplica después, en memoria.
   *
   * Un fallo acá NO rompe la bandeja ni avisa: se queda sin índice, y sin índice `insigniaDeLado`
   * simplemente no rotula nada. Es un adorno de lectura sobre datos que se ven igual; un cartel rojo
   * de error por no poder pintar dos etiquetas taparía los mensajes que sí importan.
   */
  useEffect(() => {
    if (campaignId === '') { setCatalogo({ campaignId: '', lados: new Map() }); return; }
    void listMarcasLevantamiento(campaignId)
      .then((ms) => setCatalogo({ campaignId, lados: indiceDeMarcasPropias(ms) }))
      .catch(() => setCatalogo({ campaignId, lados: new Map() }));
  }, [campaignId]);

  /** Los auditores del desplegable salen de lo YA leído: es la lista que de verdad sirve. */
  const auditores = [...new Map(
    filas.filter((f) => f.worker).map((f) => [f.worker!.id, f.worker!]),
  ).values()].sort((a, b) => a.firstNames.localeCompare(b.firstNames, 'es'));

  async function buscar() {
    if (campaignId === '') { setMensaje('Elige una campaña.'); return; }
    if (desde > hasta) { setMensaje('El rango de fechas está invertido.'); return; }
    setCargando(true);
    setMensaje('');
    try {
      const r = await listVisitasBandeja({
        campaignId, desde, hasta,
        pdvId: pdvId || null,
        workerId: workerId || null,
        estado: estado === '' ? null : estado,
        soloConBanderas,
      });
      setFilas(r);
      setBuscado(true);
      // Los contactos leídos se olvidan en cada búsqueda. Si no, un teléfono seguiría en pantalla
      // —y en memoria— sobre una lista que ya no es la misma, o peor: pegado a una fila que ya no
      // está. Volver a verlo cuesta un toque y deja su propia fila de auditoría, que es lo correcto.
      setContactos(new Map());
      if (r.length === 0) setMensaje('No hay visitas con esos filtros.');
    } catch (e) {
      setMensaje('Error al leer: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setCargando(false);
    }
  }

  async function confirmarAnulacion() {
    if (!anulando) return;
    const m = motivo.trim();
    if (m === '') return;
    setCargando(true);
    try {
      await anularVisita(anulando.id, m);
      setAnulando(null);
      await buscar();
      setMensaje('Visita anulada. No se borró: queda visible como anulada y sale de los conteos.');
    } catch (e) {
      setMensaje('Error al anular: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setCargando(false);
    }
  }

  /**
   * El formulario se PRECARGA con los valores actuales aunque nada esté marcado.
   *
   * No es contradictorio con el opt-in: los controles nacen deshabilitados, y lo que muestran es
   * «qué dice hoy esta visita», que es la información que hace falta para decidir si hay algo que
   * corregir. Un modal en blanco obligaría a cerrar, mirar la fila y volver a abrir.
   */
  function abrirCorreccion(v: VisitaRow) {
    setCorrigiendo(v);
    setForm(formVisitaDesde(v));
    setCamposMarcados(new Set());
    setMotivo('');
  }

  function cerrarCorreccion() {
    setCorrigiendo(null);
    setForm(null);
    setCamposMarcados(new Set());
  }

  /**
   * Marca o desmarca un campo. Al DESMARCAR se repone su valor desde el registro.
   *
   * Ver `reponerCampoVisita`: sostiene la promesa escrita en el modal, y además es lo que hace
   * verdadero el bloqueo que mira «cómo quedaría la visita».
   */
  function alternarCampo(campo: CampoCorreccionVisita, marcar: boolean) {
    setCamposMarcados((prev) => {
      const next = new Set(prev);
      if (marcar) next.add(campo); else next.delete(campo);
      return next;
    });
    if (!marcar && corrigiendo !== null) {
      setForm((prev) => (prev === null ? prev : reponerCampoVisita(corrigiendo, prev, campo)));
    }
  }

  async function guardarCorreccion() {
    if (!corrigiendo || !form) return;
    if (bloqueoCorreccion !== null) return;
    const cambios = cambiosDeVisita(corrigiendo, form, camposMarcados);
    if (Object.keys(cambios).length === 0) {
      setMensaje('Marcaste campos pero ninguno quedó con un valor distinto: no hay nada que corregir.');
      return;
    }
    setCargando(true);
    try {
      await corregirVisita(corrigiendo.id, cambios, motivo.trim());
      cerrarCorreccion();
      await buscar();
      setMensaje('Corrección guardada. Queda el valor anterior, quién y por qué, en «Historial».');
    } catch (e) {
      setMensaje('Error al corregir: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setCargando(false);
    }
  }

  async function abrirHistorial(v: VisitaRow) {
    setCargando(true);
    try {
      setHistorial({ fila: v, items: await listCorreccionesDeVisita(v.id) });
    } catch (e) {
      setMensaje('No se pudo leer el historial: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setCargando(false);
    }
  }

  async function resolver(id: string, e: 'confirmado' | 'descartado') {
    try {
      await resolverHallazgo(id, e);
      await buscar();
      setMensaje(e === 'confirmado' ? 'Marca confirmada.' : 'Hallazgo descartado.');
    } catch (err) {
      setMensaje('Error: ' + (err instanceof Error ? err.message : String(err)));
    }
  }

  async function verFoto(path: string) {
    try { window.open(await getPhotoUrl(path), '_blank'); }
    catch { setMensaje('No se pudo abrir la foto.'); }
  }

  /**
   * Pide el contacto de UNA visita. Es lo que dispara el botón, y solo el botón.
   *
   * De a una y no del lote entero: cada llamada deja una fila en `audit_log` con los ids que se
   * leyeron, así que pedir solo la que alguien quiso ver hace que ese rastro conteste de verdad
   * «quién miró el teléfono de este puestero». Ver `ContactoVisible`.
   */
  async function pedirContacto(v: VisitaRow) {
    setContactos((prev) => new Map(prev).set(v.id, { estado: 'cargando' }));
    try {
      const mapa = await getContactosVisitas([v.id]);
      setContactos((prev) => new Map(prev).set(v.id, contactoDesdeMapa(v.id, mapa)));
    } catch (e) {
      setContactos((prev) => new Map(prev).set(v.id, {
        estado: 'error',
        mensaje: 'No se pudo leer: ' + (e instanceof Error ? e.message : String(e)),
      }));
    }
  }

  function ocultarContacto(visitaId: string) {
    setContactos((prev) => {
      const next = new Map(prev);
      next.delete(visitaId);
      return next;
    });
  }

  /**
   * Los hallazgos los resuelve SOLO el jefe de operaciones.
   *
   * Es el espejo de `resolver_hallazgo` (0048), que empieza con `if not has_role('admin')`. La
   * pantalla los ofrecía con `puedeEditar('levantamiento', roles)`, que también es `true` para el
   * coordinador: pulsaba «es marca» y el servidor le contestaba «No autorizado» —«un botón que el
   * servidor va a rechazar es una trampa» (BITACORA §2)—. Confirmar una marca alimenta el catálogo
   * de TODAS las campañas, y por eso es del jefe de operaciones y no de quien coordina una.
   */
  const puedeResolverHallazgos = esAdmin;

  /** Lo que se ve: lo buscado, recortado por la categoría elegida. */
  const visibles = filtrarPorCategoria(filas, categoria);
  const resumen = resumenDeBandeja(visibles);
  /** Cuántas medidas no entran en NINGUNA categoría porque son anteriores a la 0060. */
  const sinCategoria = medidasSinCategoria(filas);

  // Sin modal abierto no hay formulario que revisar, y el bloqueo se calcula igual: el botón que lo
  // usa tampoco está en pantalla.
  const bloqueoCorreccion = bloqueoDeCorreccionVisita(
    camposMarcados, motivo, form ?? undefined, corrigiendo?.estado,
  );

  /**
   * Los mercados del desplegable de corrección: los de la campaña MÁS el de esta visita.
   *
   * El de la visita puede no estar en `levantamiento_mercados` —la mayoría del trabajo real se
   * registró con el mercado escrito a mano (0056)—, y un select que no contiene el valor actual se
   * pinta vacío: parecería que la visita no tiene mercado justo mientras se decide si cambiarlo.
   */
  const mercadosDelModal = corrigiendo?.pdv && !mercados.some((m) => m.id === corrigiendo.pdv!.id)
    ? [...mercados, { id: corrigiendo.pdv.id, code: '', nombre: corrigiendo.pdv.name }]
    : mercados;

  return (
    <div className="p-6">
      <h1 className="mb-1 text-xl font-semibold">Levantamiento de canal</h1>
      <p className="mb-4 max-w-3xl text-sm text-gray-600">
        Una fila por <strong>puesto medido</strong> dentro del mercado. Se puede{' '}
        <strong>corregir</strong> lo que ubica y clasifica la visita —fecha, mercado, puesto,
        auditor, observaciones— con motivo obligatorio y dejando el valor anterior en «Historial».
        Lo que el auditor <strong>observó</strong> (la cuadrícula, los precios, las promociones y
        los hallazgos) no se edita: si eso está mal, se anula la visita y se vuelve a medir.
      </p>
      <p className="mb-4 max-w-3xl text-sm text-gray-600">
        Los precios se muestran <strong>tal como se anotaron y divididos por unidad</strong>, que es
        la única forma de comparar un paquete de 4 con uno de 44. El{' '}
        <strong>contacto del puestero no se muestra</strong>: es dato personal de alguien que no
        usa el sistema, hay que pedirlo fila por fila y cada lectura queda registrada.
      </p>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-sm">
          Campaña
          <select className="rounded border px-2 py-1" value={campaignId}
            onChange={(e) => setCampaignId(e.target.value)}>
            <option value="">— Selecciona —</option>
            {campanias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>
        </label>
        <label className="flex flex-col text-sm">
          Desde
          <input type="date" className="rounded border px-2 py-1" value={desde}
            onChange={(e) => setDesde(e.target.value)} />
        </label>
        <label className="flex flex-col text-sm">
          Hasta
          <input type="date" className="rounded border px-2 py-1" value={hasta}
            onChange={(e) => setHasta(e.target.value)} />
        </label>
        <label className="flex flex-col text-sm">
          Mercado
          <select className="rounded border px-2 py-1" value={pdvId}
            disabled={mercados.length === 0}
            onChange={(e) => setPdvId(e.target.value)}>
            <option value="">Todos</option>
            {mercados.map((m) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
          </select>
        </label>
        <label className="flex flex-col text-sm">
          Auditor
          <select className="rounded border px-2 py-1" value={workerId}
            disabled={auditores.length === 0}
            onChange={(e) => setWorkerId(e.target.value)}>
            <option value="">Todos</option>
            {auditores.map((a) => (
              <option key={a.id} value={a.id}>{a.firstNames} {a.lastNames}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col text-sm">
          Estado
          <select className="rounded border px-2 py-1" value={estado}
            onChange={(e) => setEstado(e.target.value as '' | 'medido' | 'no_pude')}>
            <option value="">Todos</option>
            <option value="medido">Medidos</option>
            <option value="no_pude">No se pudo</option>
          </select>
        </label>
        <button type="button" onClick={() => void buscar()} disabled={cargando}
          className="rounded bg-blue-600 px-4 py-1.5 text-white disabled:opacity-50">
          {cargando ? 'Buscando…' : 'Buscar'}
        </button>
        <label className="flex items-center gap-1.5 text-sm">
          <input type="checkbox" checked={soloConBanderas}
            onChange={(e) => setSoloConBanderas(e.target.checked)} />
          Solo con alertas
        </label>
        {/* Va DESPUÉS del botón y no con los demás filtros: los de arriba deciden qué se le pide al
            servidor y necesitan «Buscar»; este recorta lo que ya está en pantalla y se aplica solo.
            Juntarlos haría creer que hay que volver a buscar para cambiar de categoría, que es
            justamente la comparación que se hace en voz alta en una reunión. */}
        <label className="flex flex-col text-sm">
          Categoría
          <select className="rounded border px-2 py-1" value={categoria}
            aria-label="Categoría de producto"
            title="Filtra lo ya buscado, sin volver a consultar."
            onChange={(e) => setCategoria(e.target.value as CategoriaProducto | '')}>
            <option value="">Todas</option>
            {CATEGORIAS.map((c) => (
              <option key={c.valor} value={c.valor}>{c.label}</option>
            ))}
          </select>
        </label>
      </div>

      {mensaje !== '' && (
        <p className="mb-4 rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
          {mensaje}
        </p>
      )}

      {buscado && filas.length > 0 && (
        <p className="mb-3 text-sm text-gray-700">
          <strong>{resumen.visitas}</strong> visitas · <strong>{resumen.medidas}</strong>{' '}
          puestos medidos · <strong>{resumen.noPude}</strong> no se pudo ·{' '}
          <strong>{resumen.conBandera}</strong> con alerta
          {/* El quiebre se nombra siempre, incluso en cero: es el número que hoy sale en cero en
              todo lo medido y que hay que estar mirando para cuando deje de salirlo. Escondido
              mientras vale cero, nadie se entera del primero. */}
          {' · '}
          <strong className={resumen.quiebres > 0 ? 'text-orange-700' : ''}>
            {resumen.quiebres}
          </strong>{' '}
          <span title="Marcas que el puesto trabaja y que hoy estaban agotadas. No cuenta las de
            visitas anuladas.">quiebres</span>
          {resumen.hallazgosPendientes > 0 && (
            <> · <strong className="text-purple-800">{resumen.hallazgosPendientes}</strong> marcas
              nuevas por confirmar</>
          )}
          {!puedeAccionar && ' · solo lectura'}
          {/* Se dice POR QUÉ faltan los botones, en vez de dejar que parezca un error. El caso real
              es el supervisor: comparte el rol `coordinador` con el coordinador designado, así que
              ve la bandeja completa y solo puede accionar donde él es el designado. */}
          {puedeAccionar && !esAdmin && !esValidador && filas.some(
            (f) => !puedeAccionarFilaVisita(esAdmin, esValidador, campaniasCoordinadas, f.campaignId),
          ) && ' · corregir y anular solo en las campañas que coordinas'}
        </p>
      )}

      {/* Lo que el filtro de categoría deja fuera se DICE. Una visita anterior a la 0060 no declaró
          categorías, así que no entra en ninguna: sin este aviso, filtrar por «Papel higiénico»
          hace desaparecer las 18 visitas reales del 19-ago y se lee como que no se midió papel. */}
      {buscado && categoria !== '' && sinCategoria > 0 && (
        <p className="mb-3 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
          <strong>{sinCategoria}</strong> visitas medidas quedan fuera de este filtro porque no
          declararon categorías: son anteriores al rediseño del formulario y solo tienen la pregunta
          vieja «¿qué vende?». Quita el filtro para verlas.
        </p>
      )}

      {buscado && categoria !== '' && filas.length > 0 && visibles.length === 0 && (
        <p className="mb-3 text-sm text-gray-600">
          Ninguna de las {filas.length} visitas encontradas declaró vender{' '}
          {etiquetaCategoria(categoria).toLowerCase()}.
        </p>
      )}

      {visibles.length > 0 && (
        <div className="overflow-x-auto rounded border">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-600">
              <tr>
                <th className="px-3 py-2">Fecha · puesto</th>
                <th className="px-3 py-2">Mercado</th>
                <th className="px-3 py-2">Auditor</th>
                {/* Sigue diciendo «Perfil» y no «Qué vende»: además de las categorías, la celda
                    lleva a quién le vende y qué promociones tenía. */}
                <th className="px-3 py-2">Perfil</th>
                <th className="px-3 py-2">Observaciones y cuadrícula</th>
                <th className="px-3 py-2">Precios</th>
                <th className="px-3 py-2">Recomienda</th>
                {/* Los hallazgos salieron de la columna «Estado»: una marca nueva por confirmar es
                    una tarea de alguien, no una bandera de calidad del dato, y mezcladas se
                    perdían entre «Sin GPS» y «Sin programación», que salen en casi todas. */}
                <th className="px-3 py-2">Marcas nuevas</th>
                <th className="px-3 py-2">Estado</th>
                <th className="px-3 py-2">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {visibles.map((v) => (
                <tr key={v.id} className={v.estado === 'anulado' ? 'bg-gray-50 text-gray-500' : ''}>
                  {/* El `whitespace-nowrap` va en la FECHA, no en la celda: el nombre del puesto
                      es texto libre («el de doña Rosa, esquina del pasillo 3») y sin envolver
                      estiraría la columna hasta empujar el resto de la tabla fuera de la vista. */}
                  <td className="px-3 py-2 align-top">
                    <span className="whitespace-nowrap">{v.fecha}</span>
                    <div className="text-xs text-gray-500">puesto n.º {v.numeroRecorrido}</div>
                    {/* El número se repite dentro de un mercado y se saltea —es el número real
                        escrito en el puesto—, así que el nombre es lo único que deja distinguir
                        dos puestos distintos de una misma visita cargada dos veces. */}
                    {v.nombrePuesto !== null && v.nombrePuesto.trim() !== '' && (
                      <div className="text-xs text-gray-700">{v.nombrePuesto}</div>
                    )}
                  </td>
                  {/* El PDV manda, y si no hay, el nombre ESCRITO A MANO. Pintar solo `pdv.name`
                      dejaba en «—» todos los mercados que no están en el maestro — que hoy son la
                      mayoría del trabajo real: el 19-ago solo se veía Unicachi (DEMO), el único
                      mercado dado de alta, mientras Túpac Amaru y Naranjal, con 18 puestos
                      medidos entre los dos, salían en blanco. El dato siempre estuvo en la
                      consulta; lo que faltaba era mostrarlo. */}
                  <td className="px-3 py-2 align-top">
                    {v.pdv?.name ?? ((v.mercadoLibre ?? '').trim() || '—')}
                    {v.pdv === null && (v.mercadoLibre ?? '').trim() !== '' && (
                      <div className="text-xs text-gray-500">escrito a mano</div>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top">
                    {/* El nombre DECLARADO manda: la cuenta puede ser genérica y compartida,
                        así que `worker` dice con qué acceso entró, no quién midió. */}
                    {v.auditorDeclarado ?? (v.worker ? `${v.worker.firstNames} ${v.worker.lastNames}` : '—')}
                    {v.worker && (
                      <div className="text-xs text-gray-500">
                        cuenta {v.worker.dni}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top">
                    {v.estado === 'no_pude' ? (
                      <span className="text-xs text-gray-500">
                        {etiqueta(MOTIVOS_NO_PUDE, v.motivoNoPude)}
                      </span>
                    ) : (
                      <>
                        {/* Las CATEGORÍAS mandan sobre el giro: son la respuesta que decide qué se
                            le preguntó a este puesto, y son varias a la vez. El giro se pinta solo
                            cuando no hay ninguna —las visitas anteriores al rediseño, y la de
                            abarrotes, que no se pudo derivar—, rotulado para que no parezca que a
                            unas filas les falta el dato nuevo por error. */}
                        {v.categorias.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {v.categorias.map((c) => (
                              <span key={c}
                                className="rounded bg-slate-100 px-1 py-0.5 text-xs text-slate-800">
                                {etiquetaCategoria(c)}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <div className="text-xs"
                            title="Pregunta anterior al rediseño: una sola opción, sin distinguir pañales de toallas.">
                            {etiqueta(GIROS, v.giro)}{' '}
                            <span className="text-gray-400">(giro)</span>
                          </div>
                        )}
                        <div className="mt-0.5 text-xs text-gray-500">
                          {etiqueta(CLIENTES, v.clientePrincipal)}
                        </div>
                        {/* Las promociones separan lo propio de lo ajeno: es lo que el
                            cliente quiere leer de un vistazo. */}
                        {v.promociones.map((pr, i) => (
                          <div key={`${pr.marcaNombre}-${i}`}
                            className={`mt-1 text-xs ${
                              pr.deQuien === 'propia' ? 'text-blue-800' : 'text-amber-800'}`}>
                            <span className="font-medium">
                              {pr.deQuien === 'propia' ? 'Nuestra' : 'Competencia'} ·{' '}
                              {pr.marcaNombre}
                            </span>{' '}
                            {pr.descripcion}
                            <FotosDeCampo paths={pr.fotoPaths} onVer={(p) => void verFoto(p)}
                              className="ml-1 underline" />
                          </div>
                        ))}
                        {v.hayPromocion === false && (
                          <div className="mt-1 text-xs text-gray-400">Sin promociones</div>
                        )}
                      </>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top">
                    {/* Las observaciones van con el NOMBRE de su sección: una nota suelta no
                        dice de qué pregunta habla, y al leer 300 fichas eso la vuelve inútil. */}
                    {v.observaciones.length > 0 && (
                      <div className="mb-2 rounded border border-sky-200 bg-sky-50 px-1.5 py-1">
                        {v.observaciones.map((o) => (
                          <div key={o.seccion} className="text-xs text-sky-900">
                            <span className="font-medium">{o.seccion}:</span> {o.texto}
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="space-y-0.5">
                      {v.mediciones.map((m) => (
                        <LineaMedicion key={m.marcaNombre} m={m}
                          lado={insigniaDeLado(m.marcaNombre, ladosDeFila(catalogo, v.campaignId))} />
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2 align-top">
                    {v.precios.map((p, i) => (
                      <LineaPrecio key={`${p.marcaNombre}-${i}`} p={p}
                        lado={insigniaDeLado(p.marcaNombre, ladosDeFila(catalogo, v.campaignId))}
                        onVer={(f) => void verFoto(f)} />
                    ))}
                  </td>
                  <td className="px-3 py-2 align-top">
                    {v.recomendacionDeclinada ? (
                      <span className="text-xs text-gray-500">No quiso responder</span>
                    ) : v.marcaRecomendada ? (
                      <>
                        <div className="text-xs font-medium">{v.marcaRecomendada}</div>
                        <div className="text-xs text-gray-500">
                          {etiqueta(MOTIVOS_RECOMENDACION, v.motivoRecomendacion)}
                        </div>
                      </>
                    ) : <span className="text-xs text-gray-400">—</span>}
                  </td>
                  <td className="px-3 py-2 align-top">
                    {v.hallazgos.length === 0
                      ? <span className="text-xs text-gray-400">—</span>
                      : v.hallazgos.map((h) => (
                        <LineaHallazgo key={h.id} h={h} puedeResolver={puedeResolverHallazgos}
                          onVer={(p) => void verFoto(p)}
                          onResolver={(id, e) => void resolver(id, e)} />
                      ))}
                  </td>
                  <td className="px-3 py-2 align-top">
                    {v.estado === 'anulado' && (
                      <span className="mb-1 block rounded bg-red-100 px-1.5 py-0.5 text-xs text-red-800"
                        title={v.anulacionMotivo ?? ''}>
                        Anulada
                      </span>
                    )}
                    {v.flags.map((f) => {
                      const b = BANDERA_LABEL[f] ?? { label: f, titulo: '' };
                      return (
                        <span key={f} title={b.titulo}
                          className="mb-1 block rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">
                          {b.label}
                        </span>
                      );
                    })}
                  </td>
                  <td className="px-3 py-2 align-top whitespace-nowrap">
                    {/* Acá sí se avisa cuando no hay: una visita `medido` SIEMPRE tiene al menos
                        una foto del puesto (lo obliga el CHECK de la 0058), así que la celda
                        vacía se leería como un fallo del panel. En una `no_pude` o anulada es lo
                        normal, y el gris tenue lo dice sin gritarlo. */}
                    <FotosDeCampo paths={v.fotoPuestoPaths} onVer={(p) => void verFoto(p)}
                      className="block text-xs text-blue-600 underline"
                      vacio={<span className="block text-xs text-gray-400">sin foto</span>} />
                    {/* El historial se ofrece SIEMPRE, incluso en solo lectura y en las campañas
                        que no se coordinan: leer qué se corrigió y por qué es justamente lo que
                        vuelve auditable la corrección, y esconderlo dejaría la parte que da
                        confianza visible solo para quien ya podía cambiar el dato. */}
                    <button type="button" onClick={() => void abrirHistorial(v)}
                      className="block text-xs text-blue-600 underline">Historial</button>
                    {puedeAccionar
                      && puedeAccionarFilaVisita(
                        esAdmin, esValidador, campaniasCoordinadas, v.campaignId) && (
                      <>
                        {v.estado !== 'anulado' && (
                          <>
                            <button type="button" onClick={() => abrirCorreccion(v)}
                              className="block text-xs text-blue-600 underline">Corregir</button>
                            <button type="button"
                              onClick={() => { setMotivo(''); setAnulando(v); }}
                              className="block text-xs text-red-600 underline">Anular</button>
                          </>
                        )}
                        {/* El contacto se ofrece incluso en una visita anulada: la ficha ya no
                            cuenta, pero el puestero sigue siendo el mismo y a veces es a él a quien
                            hay que volver a visitar. Lo que no cambia es que hay que PEDIRLO. */}
                        <BloqueContacto estado={contactos.get(v.id)}
                          onPedir={() => void pedirContacto(v)}
                          onOcultar={() => ocultarContacto(v.id)} />
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {corrigiendo && form && (
        <Modal titulo="Corregir visita" ancho="ancho" onCerrar={cerrarCorreccion}>
          <p className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <strong>Marca solo los campos que vas a cambiar.</strong> Los que no marques quedan
            deshabilitados mostrando lo que dice hoy la visita, y no viajan ni aparecen en el
            historial. Un campo marcado y dejado <strong>vacío borra ese dato</strong>.
          </p>
          <p className="mb-3 text-sm text-gray-600">
            No se corrige lo que el auditor <strong>observó</strong> —la cuadrícula, los precios,
            las promociones y los hallazgos— ni la hora en que el servidor recibió la ficha: de esa
            hora sale la marca de registro tardío, y es lo que prueba cuándo se cargó de verdad.
          </p>

          <h3 className="mb-2 text-sm font-semibold text-gray-700">Dónde, cuándo y quién</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <CampoCorregible etiqueta={ETIQUETA_CAMPO_VISITA.fecha} campo="fecha"
              marcados={camposMarcados} sePuedeVaciar={sePuedeVaciarVisita}
              onAlternar={alternarCampo}>
              <input type="date" aria-label="Fecha" className="w-full rounded border px-2 py-1"
                disabled={!camposMarcados.has('fecha')} value={form.fecha}
                onChange={(e) => setForm({ ...form, fecha: e.target.value })} />
            </CampoCorregible>

            <CampoCorregible etiqueta={ETIQUETA_CAMPO_VISITA.numeroRecorrido}
              campo="numeroRecorrido" marcados={camposMarcados}
              sePuedeVaciar={sePuedeVaciarVisita} onAlternar={alternarCampo}
              ayuda="Es el número del letrero del puesto, no el orden del recorrido.">
              <input type="number" min={1} aria-label="Número del puesto"
                className="w-full rounded border px-2 py-1"
                disabled={!camposMarcados.has('numeroRecorrido')} value={form.numeroRecorrido}
                onChange={(e) => setForm({ ...form, numeroRecorrido: e.target.value })} />
            </CampoCorregible>

            <CampoCorregible etiqueta={ETIQUETA_CAMPO_VISITA.nombrePuesto} campo="nombrePuesto"
              marcados={camposMarcados} sePuedeVaciar={sePuedeVaciarVisita} ancho="completo"
              onAlternar={alternarCampo}
              ayuda="Referencia libre para reconocerlo: «el de doña Rosa», «esquina pasillo 3».">
              <input aria-label="Nombre del puesto" className="w-full rounded border px-2 py-1"
                disabled={!camposMarcados.has('nombrePuesto')} value={form.nombrePuesto}
                onChange={(e) => setForm({ ...form, nombrePuesto: e.target.value })} />
            </CampoCorregible>

            {/* Los dos mercados van juntos y con casillas separadas. El servidor exige que quede
                al menos uno (0056) y aplica primero los que traen valor, así que cambiar de un
                mercado de la lista a uno escrito a mano se hace en una sola tanda. */}
            <CampoCorregible etiqueta={ETIQUETA_CAMPO_VISITA.pdvId} campo="pdvId"
              marcados={camposMarcados} sePuedeVaciar={sePuedeVaciarVisita}
              onAlternar={alternarCampo}
              ayuda="Vacíalo si el mercado va escrito a mano.">
              <select aria-label="Mercado de la lista" className="w-full rounded border px-2 py-1"
                disabled={!camposMarcados.has('pdvId')} value={form.pdvId}
                onChange={(e) => setForm({ ...form, pdvId: e.target.value })}>
                <option value="">— ninguno (escrito a mano) —</option>
                {mercadosDelModal.map((m) => (
                  <option key={m.id} value={m.id}>{m.nombre}</option>
                ))}
              </select>
            </CampoCorregible>

            <CampoCorregible etiqueta={ETIQUETA_CAMPO_VISITA.mercadoLibre} campo="mercadoLibre"
              marcados={camposMarcados} sePuedeVaciar={sePuedeVaciarVisita}
              onAlternar={alternarCampo}
              ayuda="No da de alta ningún punto de venta: queda como texto.">
              <input aria-label="Mercado escrito a mano" className="w-full rounded border px-2 py-1"
                disabled={!camposMarcados.has('mercadoLibre')} value={form.mercadoLibre}
                onChange={(e) => setForm({ ...form, mercadoLibre: e.target.value })} />
            </CampoCorregible>

            <CampoCorregible etiqueta={ETIQUETA_CAMPO_VISITA.auditorDeclarado}
              campo="auditorDeclarado" marcados={camposMarcados} ancho="completo"
              sePuedeVaciar={sePuedeVaciarVisita} onAlternar={alternarCampo}
              ayuda="Mientras las cuentas sean genéricas y compartidas, esto es lo único que dice
                     quién midió.">
              <input aria-label="Auditor declarado" className="w-full rounded border px-2 py-1"
                disabled={!camposMarcados.has('auditorDeclarado')} value={form.auditorDeclarado}
                onChange={(e) => setForm({ ...form, auditorDeclarado: e.target.value })} />
            </CampoCorregible>
          </div>

          <h3 className="mb-2 mt-4 text-sm font-semibold text-gray-700">Qué puesto es</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <CampoCorregible etiqueta={ETIQUETA_CAMPO_VISITA.giro} campo="giro"
              marcados={camposMarcados} sePuedeVaciar={sePuedeVaciarVisita}
              onAlternar={alternarCampo}>
              <select aria-label="Qué vende" className="w-full rounded border px-2 py-1"
                disabled={!camposMarcados.has('giro')} value={form.giro}
                onChange={(e) => setForm({ ...form, giro: e.target.value })}>
                <option value="">— sin responder —</option>
                {GIROS.map((g) => <option key={g.valor} value={g.valor}>{g.label}</option>)}
              </select>
            </CampoCorregible>

            <CampoCorregible etiqueta={ETIQUETA_CAMPO_VISITA.clientePrincipal}
              campo="clientePrincipal" marcados={camposMarcados}
              sePuedeVaciar={sePuedeVaciarVisita} onAlternar={alternarCampo}>
              <select aria-label="A quién le vende" className="w-full rounded border px-2 py-1"
                disabled={!camposMarcados.has('clientePrincipal')} value={form.clientePrincipal}
                onChange={(e) => setForm({ ...form, clientePrincipal: e.target.value })}>
                <option value="">— sin responder —</option>
                {CLIENTES.map((c) => <option key={c.valor} value={c.valor}>{c.label}</option>)}
              </select>
            </CampoCorregible>

            <CampoCorregible etiqueta={ETIQUETA_CAMPO_VISITA.motivoNoPude} campo="motivoNoPude"
              marcados={camposMarcados} sePuedeVaciar={sePuedeVaciarVisita} ancho="completo"
              onAlternar={alternarCampo}
              ayuda="Solo aplica a las visitas «no pude»: una visita medida no lleva motivo.">
              <select aria-label="Motivo de no pude" className="w-full rounded border px-2 py-1"
                disabled={!camposMarcados.has('motivoNoPude')} value={form.motivoNoPude}
                onChange={(e) => setForm({ ...form, motivoNoPude: e.target.value })}>
                <option value="">— sin motivo —</option>
                {MOTIVOS_NO_PUDE.map((m) => (
                  <option key={m.valor} value={m.valor}>{m.label}</option>
                ))}
              </select>
            </CampoCorregible>
          </div>

          <h3 className="mb-2 mt-4 text-sm font-semibold text-gray-700">Qué recomienda</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <CampoCorregible etiqueta={ETIQUETA_CAMPO_VISITA.marcaRecomendada}
              campo="marcaRecomendada" marcados={camposMarcados}
              sePuedeVaciar={sePuedeVaciarVisita} onAlternar={alternarCampo}>
              <input aria-label="Marca que recomienda" className="w-full rounded border px-2 py-1"
                disabled={!camposMarcados.has('marcaRecomendada')} value={form.marcaRecomendada}
                onChange={(e) => setForm({ ...form, marcaRecomendada: e.target.value })} />
            </CampoCorregible>

            <CampoCorregible etiqueta={ETIQUETA_CAMPO_VISITA.motivoRecomendacion}
              campo="motivoRecomendacion" marcados={camposMarcados}
              sePuedeVaciar={sePuedeVaciarVisita} onAlternar={alternarCampo}>
              <select aria-label="Por qué la recomienda" className="w-full rounded border px-2 py-1"
                disabled={!camposMarcados.has('motivoRecomendacion')}
                value={form.motivoRecomendacion}
                onChange={(e) => setForm({ ...form, motivoRecomendacion: e.target.value })}>
                <option value="">— sin responder —</option>
                {MOTIVOS_RECOMENDACION.map((m) => (
                  <option key={m.valor} value={m.valor}>{m.label}</option>
                ))}
              </select>
            </CampoCorregible>

            <CampoCorregible etiqueta={ETIQUETA_CAMPO_VISITA.recomendacionDeclinada}
              campo="recomendacionDeclinada" marcados={camposMarcados} ancho="completo"
              sePuedeVaciar={sePuedeVaciarVisita} onAlternar={alternarCampo}
              ayuda="«No quiso responder» es un dato, no un hueco: distingue el silencio del
                     puestero de una respuesta inventada.">
              <select aria-label="No quiso responder" className="w-full rounded border px-2 py-1"
                disabled={!camposMarcados.has('recomendacionDeclinada')}
                value={form.recomendacionDeclinada}
                onChange={(e) => setForm({ ...form, recomendacionDeclinada: e.target.value })}>
                <option value="false">No: sí respondió</option>
                <option value="true">Sí: no quiso responder</option>
              </select>
            </CampoCorregible>
          </div>

          <h3 className="mb-2 mt-4 text-sm font-semibold text-gray-700">
            Observaciones por sección
          </h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            {SECCIONES_OBSERVABLES.map((k) => (
              <CampoCorregible key={k} etiqueta={TITULO_DE_SECCION[k]} campo={k}
                marcados={camposMarcados} sePuedeVaciar={sePuedeVaciarVisita}
                onAlternar={alternarCampo}>
                <textarea rows={2} aria-label={`Observación de ${TITULO_DE_SECCION[k]}`}
                  className="w-full rounded border px-2 py-1"
                  disabled={!camposMarcados.has(k)} value={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
              </CampoCorregible>
            ))}
          </div>

          <label className="mt-4 flex flex-col text-sm">
            Motivo de la corrección <span className="text-xs text-gray-500">(obligatorio)</span>
            <textarea rows={2} className="rounded border px-2 py-1" value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ej.: se registró en la campaña de prueba y era la campaña real" />
          </label>

          <div className="mt-4 flex items-center justify-end gap-3">
            {/* El porqué se dice ANTES de pulsar: un botón apagado sin explicación se lee como
                una pantalla rota. */}
            {bloqueoCorreccion !== null && (
              <span className="text-xs text-gray-600">{bloqueoCorreccion}</span>
            )}
            <button type="button" onClick={cerrarCorreccion}
              className="rounded border px-3 py-1.5 text-sm">Cancelar</button>
            <button type="button" onClick={() => void guardarCorreccion()}
              disabled={cargando || bloqueoCorreccion !== null}
              className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white disabled:opacity-50">
              Guardar corrección
            </button>
          </div>
        </Modal>
      )}

      {historial && (
        <Modal titulo="Historial de cambios" ancho="ancho" onCerrar={() => setHistorial(null)}>
          <p className="mb-3 text-sm text-gray-600">
            {historial.fila.fecha} · puesto n.º {historial.fila.numeroRecorrido} ·{' '}
            {historial.fila.pdv?.name ?? ((historial.fila.mercadoLibre ?? '').trim() || '—')}
          </p>
          {historial.items.length === 0 ? (
            <p className="text-sm text-gray-500">
              Esta visita no tiene correcciones: está tal como la reportó el campo.
            </p>
          ) : (
            <ul className="space-y-3 text-sm">
              {historial.items.map((c) => (
                <li key={c.id} className="rounded border px-3 py-2">
                  <div className="font-medium">{CAMPO_VISITA_LABEL[c.campo] ?? c.campo}</div>
                  <div className="flex flex-wrap items-center gap-1.5 text-gray-700">
                    <span className="text-gray-500 line-through">{c.valorAnterior ?? '—'}</span>
                    <span aria-hidden="true">→</span>
                    <strong>{c.valorNuevo ?? '—'}</strong>
                  </div>
                  <div className="mt-1 text-xs text-gray-600">Motivo: {c.motivo}</div>
                  <div className="text-xs text-gray-500">
                    {c.actor ? `${c.actor.firstNames} ${c.actor.lastNames}` : 'actor desconocido'}
                    {' · '}
                    {formatInTimeZone(new Date(c.createdAt), TZ, 'dd-MM-yyyy HH:mm')}
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-4 flex justify-end">
            <button type="button" onClick={() => setHistorial(null)}
              className="rounded border px-3 py-1.5 text-sm">Cerrar</button>
          </div>
        </Modal>
      )}

      {anulando && (
        <Modal titulo="Anular visita" onCerrar={() => setAnulando(null)}>
          <p className="mb-3 text-sm text-gray-600">
            La visita no se borra: queda como anulada, con el motivo y quién la anuló, y sale de
            los conteos. Si lo que está mal es la fecha, el mercado, el puesto o una observación,
            <strong> no hace falta anular</strong>: eso se arregla con «Corregir». Anular es para
            cuando la medición en sí no debía existir —el puesto se midió dos veces, o lo que se
            observó no era de ese puesto—.
          </p>
          <label className="flex flex-col text-sm">
            Motivo <span className="text-xs text-gray-500">(obligatorio)</span>
            <textarea rows={3} className="rounded border px-2 py-1" value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ej.: se midió dos veces el mismo puesto" />
          </label>
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" onClick={() => setAnulando(null)}
              className="rounded border px-3 py-1.5 text-sm">Cancelar</button>
            <button type="button" onClick={() => void confirmarAnulacion()}
              disabled={cargando || motivo.trim() === ''}
              className="rounded bg-red-600 px-3 py-1.5 text-sm text-white disabled:opacity-50">
              Anular
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
